/**
 * Workbench "Export CSV" transaction rows (court documentation export).
 *
 * The last column, "Section 65B Evidence Reference", names each row as an
 * electronic record: `<chain>:<tx_hash>`, followed by the saved investigation
 * snapshot and case it was exported from when the Workbench has them. The
 * HTML LE report carries the certificate itself (renderSection65BCertificate,
 * BSA s.63, formerly Evidence Act s.65B); this reference is what lets a row in
 * the spreadsheet be tied back to that certificate and to the chain.
 */

export const SECTION_65B_COLUMN = 'Section 65B Evidence Reference';

export const TX_CSV_HEADER = [
    'Tx Hash', 'Chain', 'From', 'To', 'Value', 'Token', 'Fee', 'Timestamp',
    'Block Height', 'Historical USD', 'From Entity', 'To Entity', 'Direction',
    'Queried Address', SECTION_65B_COLUMN,
].join(',');

/** CSV-safe value escaper. */
export function csvEscape(v: unknown): string {
    if (v == null || v === '') return '';
    const s = String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
}

/** Seconds-epoch, ms-epoch or date string → ISO; unparseable passes through. */
export function csvTimestamp(ts: unknown): string {
    if (ts == null) return '';
    const num = Number(ts);
    const d = !isNaN(num) && num > 1e9 ? new Date(num * 1000) : new Date(ts as any);
    return !isNaN(d.getTime()) ? d.toISOString() : String(ts);
}

export interface EvidenceRefContext {
    caseId?: string | null;
    investigationId?: string | null;
}

/** `<chain>:<tx_hash>[ | investigation <id>][ | case <id>]`; empty when there is no hash. */
export function section65BEvidenceRef(chain: string | null | undefined, txHash: string | null | undefined, ctx: EvidenceRefContext = {}): string {
    const hash = (txHash || '').trim();
    if (!hash) return '';
    const parts = [`${(chain || 'UNKNOWN').toUpperCase()}:${hash}`];
    if (ctx.investigationId) parts.push(`investigation ${ctx.investigationId}`);
    if (ctx.caseId) parts.push(`case ${ctx.caseId}`);
    return parts.join(' | ');
}

/** One transaction row, columns in TX_CSV_HEADER order, unescaped. */
export function txExportCells(tx: Record<string, any>, txChain: string, queriedAddress: string, ctx: EvidenceRefContext = {}): string[] {
    return [
        tx.tx_hash ?? '',
        txChain,
        tx.from_address ?? '',
        tx.to_address ?? '',
        tx.value ?? '',
        tx.token_symbol ?? '',
        tx.fee ?? '',
        csvTimestamp(tx.timestamp),
        tx.block_height ?? '',
        tx.historical_usd ?? '',
        tx.from_entity_name ?? tx.counterparty_entity ?? '',
        tx.to_entity_name ?? '',
        tx.direction ?? '',
        queriedAddress,
        section65BEvidenceRef(txChain, tx.tx_hash, ctx),
    ].map((v) => (v == null ? '' : String(v)));
}

/** One transaction row, columns in TX_CSV_HEADER order. */
export function buildTxCsvRow(tx: Record<string, any>, txChain: string, queriedAddress: string, ctx: EvidenceRefContext = {}): string {
    return txExportCells(tx, txChain, queriedAddress, ctx).map(csvEscape).join(',');
}

function xmlEscape(v: unknown): string {
    return String(v ?? '')
        // XML 1.0 rejects these controls. A wallet label can carry one from a scraped name.
        .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F]/g, '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

/** Excel sheet names are 31 characters and cannot contain : \ / ? * [ ]. */
function excelSheetName(name: string): string {
    const cleaned = name.replace(/[:\\/?*[\]]/g, ' ').trim().slice(0, 31);
    return xmlEscape(cleaned || 'Sheet');
}

/**
 * Excel 2003 SpreadsheetML. Excel opens it as a workbook. No zip and no
 * extra library: the hosted frontend does not ship a spreadsheet package.
 * Sheet names are truncated to Excel's 31-character limit.
 */
export function spreadsheetMl(sheets: { name: string; rows: unknown[][] }[]): string {
    const body = sheets.map((sheet) => {
        const rows = sheet.rows.map((row) => {
            const cells = row.map((cell) => `<Cell><Data ss:Type="String">${xmlEscape(cell)}</Data></Cell>`).join('');
            return `<Row>${cells}</Row>`;
        }).join('');
        return `<Worksheet ss:Name="${excelSheetName(sheet.name)}"><Table>${rows}</Table></Worksheet>`;
    }).join('');
    return (
        `<?xml version="1.0"?>\n` +
        `<?mso-application progid="Excel.Sheet"?>\n` +
        `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
        `${body}</Workbook>`
    );
}
